// src/components/features/timetables/Notes/TimetableNotes.tsx
import type { ComponentProps } from 'preact';
import type { TimetableNotice } from '../../../../arcticapi/types';
import { Heading } from '../../../base/Heading/Heading';
import styles from './TimetableNotes.module.css';

export interface TimetableNotesProps extends Omit<
  ComponentProps<'section'>,
  'children'
> {
  notices: TimetableNotice[];
}

/** The id a journey header's note code links to, and this note's entry uses as its anchor. */
export function noteAnchorId(noticeCode: string): string {
  return `note-${noticeCode}`;
}

/**
 * Re-triggers a note's highlight animation on a click that doesn't change
 * the URL hash, i.e. the code is already the current `:target` so a second
 * click would otherwise just scroll without re-highlighting.
 *
 * Reduced-motion readers get a static highlight via `:target` alone (no
 * animation to replay), so this is a no-op for them.
 */
export function replayNoteHighlight(noticeCode: string): void {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  const target = document.getElementById(noteAnchorId(noticeCode));
  if (!target) {
    return;
  }

  target.classList.remove(styles.replay);
  // Force a reflow so re-adding the class restarts the animation.
  void target.offsetWidth;
  target.classList.add(styles.replay);
}

/**
 * Note definitions shown under the timetable.
 *
 * Always visible when there are notices, so the text is useful even before a
 * reader follows a code from the grid header. Each entry's id is the anchor
 * target for the matching header link, so following one jumps and highlights
 * it via the `:target` selector, with no scripting required.
 */
export function TimetableNotes({
  notices,
  className = '',
  ...sectionProps
}: TimetableNotesProps) {
  if (notices.length === 0) {
    return null;
  }

  return (
    <section
      className={`${styles.root} ${className}`.trim()}
      aria-label="Notes"
      data-component="Timetable Notes"
      {...sectionProps}
    >
      <Heading as="h5" variant="sm">
        Notes
      </Heading>
      <dl className={styles.list}>
        {notices.map((notice) => (
          <div
            key={notice.noticeCode}
            id={noteAnchorId(notice.noticeCode)}
            className={styles.entry}
            tabIndex={-1}
          >
            <dt className={styles.code}>{notice.noticeCode}</dt>
            <dd className={styles.text}>{notice.noticeText}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
